import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { InputSelectComponent } from '@components/input-select/input-select.component';
import { LoadingComponent } from '@components/loading/loading.component';
import { MESSAGES } from '@constants-globals/messages';
import { UpdateJobOfferData } from '@models/jobOffer-payload-model';
import { NgIcon } from '@ng-icons/core';
import { ToastService } from '@services/toast.service';
import { finalize, forkJoin } from 'rxjs';
import {
  AreaService,
  JobTitleService,
  JobTypeService,
} from 'src/app/api/services';
import { EducationService } from 'src/app/api/services/education.service';
import { ProfesionService } from 'src/app/api/services/proffesion.service';
import { InputTextComponent } from 'src/app/shared/component/input-text/input-text.component';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { JobOffer } from 'src/app/shared/model/job-detail';

@Component({
  selector: 'app-job-offer-update',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CKEditorModule,
    LoadingButtonComponent,
    InputTextComponent,
    InputSelectComponent,
    CommonModule,
    NgIcon,
    LoadingComponent,
  ],
  templateUrl: './job-offer-update.component.html',
  styles: ``,
})
export class JobOfferUpdateComponent {
  public Editor: any = ClassicEditor;
  @Input() jobOffer!: JobOffer;
  @Input() loading = false;

  @Output() update = new EventEmitter<UpdateJobOfferData>();

  @Output() close = new EventEmitter<void>();

  jobOfferForm!: FormGroup;

  public content: string = '';

  isLoading: boolean = false;

  areas!: any[];
  employmentTypes!: any[];
  jobTitles!: any[];
  professions!: any[];
  educations: any[] = [];

  public editorConfig = {
    placeholder: 'Escribe aquí...',
    toolbar: [
      'heading',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'bulletedList',
      '|',
      'outdent',
      'indent',
      '|',
      'link',
      'insertTable',
      '|',
      'undo',
      'redo',
    ],
  };

  constructor(
    private fb: FormBuilder,
    private educationService: EducationService,
    private employmentService: JobTypeService,
    private jobTitleService: JobTitleService,
    private profesionService: ProfesionService,
    private toastService: ToastService,
    private areaService: AreaService
  ) {}

  ngOnInit(): void {
    this.createFormStructure();
    this.loadInitialData();
  }

  private ageRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const minAge = group.get('minAge')?.value;
      const maxAge = group.get('maxAge')?.value;
      return minAge && maxAge && minAge > maxAge
        ? { ageRangeInvalid: true }
        : null;
    };
  }

  private salaryRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const minSalary = group.get('minSalary')?.value;
      const maxSalary = group.get('maxSalary')?.value;
      return minSalary && maxSalary && minSalary > maxSalary
        ? { salaryRangeInvalid: true }
        : null;
    };
  }

  createFormStructure(): void {
    this.jobOfferForm = this.fb.group(
      {
        idArea: ['', Validators.required],
        idJobType: ['', Validators.required],
        idEducation: ['', Validators.required],
        idProfession: ['', Validators.required],
        idJobTitle: ['', Validators.required],
        title: [
          '',
          [
            Validators.required,
            Validators.minLength(4),
            Validators.maxLength(100),
          ],
        ],
        subTitle: [
          '',
          [
            Validators.required,
            Validators.minLength(4),
            Validators.maxLength(150),
          ],
        ],
        description: ['', [Validators.required, Validators.minLength(10)]],
        minimumSalary: ['', [Validators.min(0), Validators.max(9999)]],
        maximumSalary: ['', [Validators.min(0), Validators.max(9999)]],
        minimumAge: [
          18,
          [Validators.required, Validators.min(18), Validators.max(99)],
        ],
        maximumAge: [
          40,
          [Validators.required, Validators.min(18), Validators.max(99)],
        ],
        minimumExperience: [
          1,
          [Validators.required, Validators.min(0), Validators.max(50)],
        ],
        numberVacancies: [
          1,
          [
            Validators.required,
            Validators.pattern(/^[1-9]\d*$/),
            Validators.max(99),
          ],
        ],

        showSalary: [false],
        hasRemuneration: [false],
      },
      {
        validators: [this.ageRangeValidator(), this.salaryRangeValidator()],
      }
    );
  }

  loadInitialData(): void {
    this.isLoading = true;

    const dataRequests = [
      this.educationService.getEducations(),
      this.areaService.areaGet(),
      this.employmentService.tipoEmpleoGet(),
      this.jobTitleService.tituloEmpleoGet(),
      this.profesionService.getProfesions(),
    ];

    forkJoin(dataRequests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: ([
          educationsRes,
          areasRes,
          employmentTypesRes,
          jobTitlesRes,
          professionsRes,
        ]) => {
          this.educations = educationsRes.dto.educations.map((item: any) => ({
            value: item.idEducation,
            label: item.name,
          }));
          this.areas = areasRes.dto.areas.map((item: any) => ({
            value: item.idArea,
            label: item.name,
          }));
          this.employmentTypes = employmentTypesRes.dto.jobTypes.map(
            (item: any) => ({
              value: item.id,
              label: item.description,
            })
          );
          this.jobTitles = jobTitlesRes.dto.jobTitles.map((item: any) => ({
            value: item.idJobTitle,
            label: item.name,
          }));
          this.professions = professionsRes.dto.professions.map(
            (item: any) => ({
              value: item.idProfession,
              label: item.name,
            })
          );

          this.initForm();
        },
        error: (err) => {
          this.toastService.showMessages('error', [
            'Error al cargar datos iniciales. Intente de nuevo.',
          ]);
          console.error('Error al cargar datos iniciales:', err);
        },
      });
  }

  onUpdateClick(): void {
    if (this.jobOfferForm.invalid) {
      this.jobOfferForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const formValue = this.jobOfferForm.getRawValue();
    const hasRemuneration = formValue.hasRemuneration ? 1 : 0;

    const minimumSalary = hasRemuneration ? Number(formValue.minimumSalary) : 0;
    const maximumSalary = hasRemuneration ? Number(formValue.maximumSalary) : 0;
    const showSalary = hasRemuneration && formValue.showSalary ? 1 : 0;

    const payload: UpdateJobOfferData = {
      idArea: formValue.idArea,
      idEducation: formValue.idEducation,
      idJobType: formValue.idJobType,
      idJobTitle: formValue.idJobTitle,
      idProfession: formValue.idProfession,
      title: formValue.title,
      subTitle: formValue.subTitle,
      description: formValue.description,
      minimumAge: Number(formValue.minimumAge),
      maximumAge: Number(formValue.maximumAge),
      minimumExperience: Number(formValue.minimumExperience),
      numberVacancies: Number(formValue.numberVacancies),
      requestRemuneration: hasRemuneration,
      minimumSalary: minimumSalary,
      maximumSalary: maximumSalary,
      showSalary: showSalary,
    };

    this.update.emit(payload);
  }

  initForm(): void {
    const hasRemuneration = Number(this.jobOffer.requestRemuneration) === 1;
    const showSalary = Number(this.jobOffer.showSalary) === 1;

    if (this.jobOffer) {
      this.jobOfferForm.patchValue({
        idArea: this.jobOffer?.area?.idArea,
        idEducation: this.jobOffer?.education?.idEducation,
        idJobType: this.jobOffer?.jobType?.idJobType,
        idJobTitle: this.jobOffer?.jobTitle?.idJobTitle,
        idProfession: this.jobOffer?.profession?.idProfession,
        title: this.jobOffer?.title,
        subTitle: this.jobOffer?.subTitle,
        description: this.jobOffer?.description,
        minimumAge: this.jobOffer?.minimumAge,
        maximumAge: this.jobOffer?.maximumAge,
        minimumExperience: this.jobOffer?.minimumExperience,
        numberVacancies: this.jobOffer?.numberVacancies,
        minimumSalary: Number(this.jobOffer?.minimumSalary),
        maximumSalary: Number(this.jobOffer?.maximumSalary),
        showSalary: showSalary,
        hasRemuneration: hasRemuneration,
      });
    }

    this.handleSalaryFields(hasRemuneration);

    this.jobOfferForm
      .get('hasRemuneration')
      ?.valueChanges.subscribe((value) => {
        this.handleSalaryFields(value);
      });
  }

  onCloseClick(): void {
    this.close.emit();
  }

  handleSalaryFields(hasRemuneration: boolean): void {
    const minSalaryControl = this.jobOfferForm.get('minimumSalary');
    const maxSalaryControl = this.jobOfferForm.get('maximumSalary');
    const showSalaryControl = this.jobOfferForm.get('showSalary');

    if (!hasRemuneration) {
      minSalaryControl?.disable();
      minSalaryControl?.setValue(0);
      maxSalaryControl?.disable();
      maxSalaryControl?.setValue(0);
      showSalaryControl?.disable();
      showSalaryControl?.setValue(false);
    } else {
      minSalaryControl?.enable();
      maxSalaryControl?.enable();
      showSalaryControl?.enable();
    }
  }
}
